import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Microphone, Stop, Phone as PhoneIcon, MapPin, FileArrowUp, ClipboardText, House, NotePencil } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import QuickNoteCard from "../components/QuickNoteCard";

const CLIENT_UPLOAD_KINDS = [["discharge_instructions", "Discharge instructions"], ["med_list", "Medication list"], ["poa", "Power of Attorney"], ["other", "Other document"]];

const cname = (c) => c.forms?.intake?.full_name || c.client_code || "Client";
const cphone = (c) => c.forms?.intake?.phone || "";
const caddr = (c) => c.forms?.intake?.address || "";

function FieldCallLogCard() {
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    api.get("/call-log/permission").then((r) => setAllowed(!!r.data.allowed)).catch(() => {});
  }, []);
  if (!allowed) return null;
  return (
    <Link to="/call-log" className="flex items-center justify-between rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="field-call-log-link">
      <span className="flex items-center gap-2 text-sm font-medium text-stone-700"><NotePencil size={16} className="text-primary" /> Log a call / note</span>
      <span className="text-[11px] text-stone-400">speak it → AI files it</span>
    </Link>
  );
}

function Card({ title, hint, children, testid }) {  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <p className="font-heading text-base font-semibold text-stone-900">{title}</p>
      {hint && <p className="mt-0.5 text-xs text-stone-400">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

const selCls = "h-11 w-full rounded-xl border border-stone-300 bg-white px-3 text-sm";

export default function FieldPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [todo, setTodo] = useState(null);

  const [cdClient, setCdClient] = useState("");
  const [cdKind, setCdKind] = useState("other");
  const [busy, setBusy] = useState("");

  const [coEmp, setCoEmp] = useState("");
  const [coClient, setCoClient] = useState("");
  const [coDate, setCoDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [coReason, setCoReason] = useState("");

  const [noteClient, setNoteClient] = useState("");
  const [noteText, setNoteText] = useState("");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const [dirTab, setDirTab] = useState("clients");
  const [dirQ, setDirQ] = useState("");

  useEffect(() => {
    api.get("/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/admin/todo").then((r) => setTodo(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/users").then((r) => setStaff((Array.isArray(r.data) ? r.data : r.data.users || []).filter((u) => u.active !== false && (u.role || "staff") !== "admin"))).catch(() => {});
    }
  }, [user?.role]);

  const uploadClientDoc = async (f) => {
    if (!f) return;
    if (!cdClient) { toast.error("Pick the client first"); return; }
    setBusy("cd");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/clients/${cdClient}/upload?kind=${cdKind}`, fd, { timeout: 120000 });
      toast.success("Saved to the client's record");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const uploadInbox = async (f) => {
    if (!f) return;
    setBusy("ib");
    try {
      const fd = new FormData();
      fd.append("files", f);
      await api.post("/document-inbox", fd, { timeout: 120000 });
      toast.success("In the document inbox — assign it from Uploads when you're back");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const submitCallout = async () => {
    setBusy("co");
    try {
      await api.post("/callouts", { user_id: coEmp, client_name: coClient, date: coDate, reason: coReason.trim() });
      toast.success("Call-out logged");
      setCoEmp(""); setCoClient(""); setCoReason("");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const toggleRec = async () => {
    if (recording) { recRef.current?.stop(); setRecording(false); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "note.webm");
        setTranscribing(true);
        try {
          const { data } = await api.post("/transcribe", fd, { timeout: 120000 });
          setNoteText((t) => (t ? t + " " : "") + (data.text || ""));
          toast.success("Transcribed — review and save");
        } catch (e) { toast.error(formatApiError(e)); }
        setTranscribing(false);
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch { toast.error("Microphone access denied — check browser permission"); }
  };

  const saveNote = async () => {
    if (!noteClient) { toast.error("Pick the client first"); return; }
    if (!noteText.trim()) { toast.error("Nothing to save"); return; }
    setBusy("note");
    try {
      await api.post(`/clients/${noteClient}/notes`, { note: noteText.trim() });
      toast.success("Note saved to the client's record");
      setNoteText("");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const q = dirQ.toLowerCase();
  const dirClients = clients.filter((c) => cname(c).toLowerCase().includes(q));
  const dirStaff = staff.filter((u) => (u.name || "").toLowerCase().includes(q));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="field-page">
      <header className="sticky top-0 z-40 border-b border-stone-200/60 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-lg items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/shc-logo.png" alt="" className="h-8 w-auto" />
            <div>
              <p className="font-heading text-sm font-semibold text-stone-900">Field mode</p>
              <p className="text-[10px] text-stone-400">on the road essentials</p>
            </div>
          </div>
          <Link to="/" className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-900 hover:text-white" data-testid="field-fullsite-link">
            <House size={13} /> Full site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-4 px-4 py-5">
        <QuickNoteCard presetType="visit_note" compact />
        {todo && (
          <Link to="/" className="flex items-center justify-between rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="field-todo-chip">
            <span className="text-sm font-medium text-stone-700">To-do list</span>
            <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">{todo.total ?? 0}</span>
          </Link>
        )}

        <FieldCallLogCard />

        <Card title="Client document" hint="Snap it now — it goes straight onto the client's record." testid="field-client-doc">
          <div className="space-y-2">
            <select value={cdClient} onChange={(e) => setCdClient(e.target.value)} className={selCls} data-testid="field-doc-client">
              <option value="">Pick client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{cname(c)}</option>)}
            </select>
            <select value={cdKind} onChange={(e) => setCdKind(e.target.value)} className={selCls} data-testid="field-doc-kind">
              {CLIENT_UPLOAD_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label className={`flex h-14 cursor-pointer items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white transition-colors ${cdClient ? "bg-primary hover:bg-stone-900" : "bg-stone-300"}`} data-testid="field-doc-upload">
              <Camera size={20} weight="bold" /> {busy === "cd" ? "Uploading…" : "Snap / upload"}
              <input type="file" accept="image/*,.pdf,.heic" capture="environment" className="hidden" disabled={!cdClient || busy === "cd"}
                data-testid="field-doc-input" onChange={(e) => { uploadClientDoc(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        </Card>

        <Card title="Employee document" hint="ID, SS card, certificate — parks in the inbox; you assign it to the right person later." testid="field-inbox">
          <label className="flex h-14 cursor-pointer items-center justify-center gap-2 rounded-xl bg-stone-900 text-sm font-semibold text-white transition-colors hover:bg-primary" data-testid="field-inbox-upload">
            <FileArrowUp size={20} weight="bold" /> {busy === "ib" ? "Uploading…" : "Snap to document inbox"}
            <input type="file" accept="image/*,.pdf" capture="environment" className="hidden" disabled={busy === "ib"}
              data-testid="field-inbox-input" onChange={(e) => { uploadInbox(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </Card>

        <Card title="Report a call out" hint="Logs it on the employee's record and your to-do list." testid="field-callout">
          <div className="space-y-2">
            <select value={coEmp} onChange={(e) => setCoEmp(e.target.value)} className={selCls} data-testid="field-callout-employee">
              <option value="">Employee…</option>
              {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <select value={coClient} onChange={(e) => setCoClient(e.target.value)} className={selCls} data-testid="field-callout-client">
              <option value="">Client (optional)…</option>
              {clients.map((c) => <option key={c.id} value={cname(c)}>{cname(c)}</option>)}
            </select>
            <input type="date" value={coDate} onChange={(e) => setCoDate(e.target.value)} className={selCls} data-testid="field-callout-date" />
            <textarea rows={2} value={coReason} onChange={(e) => setCoReason(e.target.value)} placeholder="Reason…"
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="field-callout-reason" />
            <Button onClick={submitCallout} disabled={busy === "co" || !coEmp || !coReason.trim()}
              className="h-11 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-stone-900" data-testid="field-callout-submit">
              {busy === "co" ? "Saving…" : "Log call out"}
            </Button>
          </div>
        </Card>

        <Card title="Voice note" hint="Dictate it — the app types it up. Saved on the client's record." testid="field-note">
          <div className="space-y-2">
            <select value={noteClient} onChange={(e) => setNoteClient(e.target.value)} className={selCls} data-testid="field-note-client">
              <option value="">Pick client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{cname(c)}</option>)}
            </select>
            <button onClick={toggleRec} disabled={transcribing}
              className={`flex h-14 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-white transition-colors ${recording ? "animate-pulse bg-[#C0392B]" : "bg-stone-900 hover:bg-primary"} disabled:opacity-60`}
              data-testid="field-mic">
              {recording ? <><Stop size={20} weight="fill" /> Tap to stop</> : transcribing ? "Transcribing…" : <><Microphone size={20} weight="bold" /> Tap to dictate</>}
            </button>
            <textarea rows={3} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Or type the note here…"
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="field-note-text" />
            <Button onClick={saveNote} disabled={busy === "note" || !noteClient || !noteText.trim()}
              className="h-11 w-full rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-stone-900" data-testid="field-note-save">
              {busy === "note" ? "Saving…" : "Save note"}
            </Button>
          </div>
        </Card>

        <Card title="Directory" hint="Tap to call, tap the pin for directions." testid="field-directory">
          <div className="mb-2 flex gap-1 rounded-full border border-stone-200 p-0.5">
            {[["clients", "Clients"], ["staff", "Staff"]].map(([k, l]) => (
              <button key={k} onClick={() => setDirTab(k)}
                className={`flex-1 rounded-full py-1.5 text-xs font-medium transition-colors ${dirTab === k ? "bg-stone-900 text-white" : "text-stone-500"}`}
                data-testid={`field-dir-tab-${k}`}>{l}</button>
            ))}
          </div>
          <input value={dirQ} onChange={(e) => setDirQ(e.target.value)} placeholder="Search…"
            className="mb-2 h-11 w-full rounded-xl border border-stone-300 bg-white px-3 text-sm" data-testid="field-dir-search" />
          <div className="max-h-96 divide-y divide-stone-100 overflow-y-auto">
            {dirTab === "clients" ? (
              dirClients.length === 0 ? <p className="py-4 text-center text-xs text-stone-400">No matches.</p> :
              dirClients.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-2 py-2.5" data-testid={`field-dir-client-${c.id}`}>
                  <div className="min-w-0">
                    <Link to={`/clients/${c.id}`} className="block truncate text-sm font-medium text-stone-800">{cname(c)}</Link>
                    {caddr(c) && <p className="truncate text-[11px] text-stone-400">{caddr(c)}</p>}
                  </div>
                  <span className="flex shrink-0 gap-1.5">
                    {cphone(c) && <a href={`tel:${cphone(c).replace(/\D/g, "")}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" data-testid={`field-dir-call-${c.id}`}><PhoneIcon size={16} weight="bold" /></a>}
                    {caddr(c) && <a href={`https://maps.google.com/?q=${encodeURIComponent(caddr(c))}`} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-100 text-stone-600" data-testid={`field-dir-map-${c.id}`}><MapPin size={16} weight="bold" /></a>}
                  </span>
                </div>
              ))
            ) : (
              dirStaff.length === 0 ? <p className="py-4 text-center text-xs text-stone-400">No matches.</p> :
              dirStaff.map((u) => {
                const phone = u.profile?.phone || "";
                const addr = [u.profile?.address, u.profile?.state].filter(Boolean).join(", ");
                return (
                  <div key={u.id} className="flex items-center justify-between gap-2 py-2.5" data-testid={`field-dir-staff-${u.id}`}>
                    <div className="min-w-0">
                      <Link to={`/team/${u.id}`} className="block truncate text-sm font-medium text-stone-800">{u.name}</Link>
                      {addr && <p className="truncate text-[11px] text-stone-400">{addr}</p>}
                    </div>
                    <span className="flex shrink-0 gap-1.5">
                      {phone && <a href={`tel:${phone.replace(/\D/g, "")}`} className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" data-testid={`field-dir-call-${u.id}`}><PhoneIcon size={16} weight="bold" /></a>}
                      {addr && <a href={`https://maps.google.com/?q=${encodeURIComponent(addr)}`} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full bg-stone-100 text-stone-600" data-testid={`field-dir-map-${u.id}`}><MapPin size={16} weight="bold" /></a>}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        <p className="pb-6 pt-2 text-center text-[11px] text-stone-400">
          <ClipboardText size={12} className="mr-1 inline" /> Everything else is on the <Link to="/" className="font-medium text-primary hover:underline">full site</Link> — this view never takes anything away.
        </p>
      </main>
    </div>
  );
}
